import React from 'react';

interface ProvenanceChip {
  color: string;
  label: string;
}

interface InsightProvenanceFooterProps {
  chips: ProvenanceChip[];
  method: string;
  // Optional "Pilot preview" / "Stub data" ribbon — shown above the chips
  // when the underlying hook is returning stubbed or approximated data.
  stubLabel?: string;
}

// Reuses the CategoricalLegend swatch-row pattern for visual consistency.
// A required footer on every Insight card because CTrees' audience is
// scientific — showing the source layers + method builds trust in the
// derived metric and prevents "black box" framing.
export const InsightProvenanceFooter: React.FC<InsightProvenanceFooterProps> = ({
  chips,
  method,
  stubLabel,
}) => {
  return (
    <div className="pt-3 border-t border-figma-lime/20 space-y-2">
      {stubLabel && (
        // Higher-contrast treatment: solid figma-yellow background with
        // panel-dark text instead of a faint translucent ribbon. The card's
        // headline is the same yellow accent — the ribbon now reads as a
        // peer of the headline (intended visual weight: "this is the
        // important caveat") rather than blending into the chrome.
        <p
          role="note"
          className="inline-block px-2 py-1 rounded bg-figma-yellow text-[11px] font-roboto-mono font-bold text-figma-panel uppercase tracking-wider shadow-figma-card"
        >
          {stubLabel}
        </p>
      )}
      <div>
        <p className="text-[11px] font-roboto text-neutral-500 uppercase tracking-wider mb-1.5">
          Derived from
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {chips.map((chip) => (
            <div key={chip.label} className="flex items-center gap-1.5 text-[11px] font-roboto text-neutral-300">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ backgroundColor: chip.color }}
                aria-hidden="true"
              />
              <span>{chip.label}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] font-roboto text-neutral-400 italic leading-relaxed">
        {method}
      </p>
    </div>
  );
};
